import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { toast } from '@/components/ui/toaster.jsx';
import { runIatQuote } from '@/services/submission.service.js';
import { verifyFiredQuote } from '@/lib/verifyFire.js';
import { formatCurrency } from '@/lib/format.js';
import { showPresubmitRefusal } from '@/lib/presubmit-refusal.js';

// "Quote with IAT" — the second live-API door, and the one that is actually
// pointed at production (iatflightapi.com, verified 2026-08-05).
//
// EVERY PIECE OF THIS ALREADY EXISTED. POST /api/tracon/iat-quote/:dealId is a
// JWT-authed proxy that resolves the deal's session, builds the manifest into
// IAT XML, submits to FlightDeck, and hands the result to storeQuoteResult,
// which writes the ladder rung through recordApiSubmission (the one send door,
// prompt 76). `runIatQuote` was already exported from submission.service.
// The only missing piece was a control on the ladder, so the sole way to fire
// IAT was curl. This is that control and nothing more — no second pipeline,
// no second door.
//
// Deliberately NOT Global's full shape: Global needs a readiness gate and an
// option-ladder picker because it refers on multi-aircraft and returns three
// rungs to choose from. IAT returns one indication, so there is nothing to
// pick and no dialog to show.
// carriers.id for IAT. Only a FALLBACK, for a fire that reported no quote id;
// the id the server persisted is the strong match. Verified against the live
// resolver (classifyBrokerCarrierAsync('IAT') -> 3) rather than assumed.
const IAT_CARRIER_REF_ID = 3;

export default function IatQuoteAction({ dealId, onLanded, label = 'Quote with IAT' }) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!dealId) return null;

  async function fire() {
    setBusy(true);
    try {
      const res = await runIatQuote(dealId);
      // A pilot name the IAT XML printed blank (ruling 4 at the flip): the line is the broker's, never the carrier's.
      for (const w of (Array.isArray(res?.warnings) ? res.warnings : [])) toast.info(w);
      // NOT SENT: the shared pre-submit check (utils/carrier-presubmit) refused.
      if (showPresubmitRefusal(res, 'IAT', navigate)) return;
      const q = res?.quote || {};
      const total = q?.premiums?.total;
      // IAT answers "no terms" inside a 200 with messages rather than an HTTP
      // error, so a success response is NOT proof of a quote. Say which
      // happened instead of reporting every 200 as a win.
      if (!(total != null && total !== '' && Number(total) > 0)) {
        const msgs = Array.isArray(q.errors) ? q.errors : [];
        toast.info(msgs.length ? `IAT returned no premium: ${msgs.join(', ')}` : 'IAT returned no premium.');
        onLanded?.();
        return;
      }

      // A CONFIRMATION IS A READ, NOT AN ECHO (prompt 106 item 2). Everything
      // above this line is the SOAP response — what IAT said, not what TRACON
      // now holds. The premium below comes out of the store the ladder renders
      // from, so the toast can only say "quoted" about a row that is actually
      // there and actually joinable.
      const seen = await verifyFiredQuote(dealId, {
        quoteId: res?.stored?.quote_id || null,
        carrierRefId: IAT_CARRIER_REF_ID,
      });
      onLanded?.();
      if (seen.landed) {
        const prem = seen.quote?.premium != null ? formatCurrency(Number(seen.quote.premium)) : total;
        toast.success(`IAT quoted ${prem}${seen.quote?.carrier_quote_number ? ` · quote ${seen.quote.carrier_quote_number}` : ''}`);
      } else {
        // THE PLAIN STATEMENT. IAT gave a premium and TRACON does not have it;
        // that is the case where he needs to act, so it does not get softened
        // into a warning or dropped into a log.
        toast.error(`IAT quoted ${total} but it is not on this deal: ${seen.reason}.`, { duration: Infinity });
      }
    } catch (err) {
      toast.error(err.body?.error || err.message || 'IAT quote failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={fire}
      disabled={busy}
      title="Fire a live AeroQuote submission to IAT FlightDeck"
      className="inline-flex shrink-0 items-center gap-1 rounded-pill border border-accent px-2.5 py-0.5 text-[11px] text-accent hover:bg-accent hover:text-white disabled:opacity-50"
    >
      <Zap size={11} strokeWidth={1.5} />
      {busy ? 'Quoting…' : label}
    </button>
  );
}
